// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import Alert from "src/components/shared/alerts/Alert/Alert";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import CustomAutocomplete from "src/components/shared/inputs/CustomAutocomplete/CustomAutocomplete";
import CreateEMRPatientModal from "../CreateEMRPatientModal.tsx/CreateEMRPatientModal";
import ExternalExamIframe from "src/components/containers/shared/ExternalExamIframe/ExternalExamIframe";
import PatientMatchPanel from "./PatientMatchPanel/PatientMatchPanel";
import { PlusIcon } from "src/assets/icons";

// Styles
import {
  ModalCustomConfirmDarkCta,
  ModalCustomConfirmLink,
} from "src/styles/mui/muiStylesHelper";

// Hooks
import { usePatientById } from "src/services/patients/hooks/usePatientById";
import { useConfirmEMRPatient } from "src/services/emr/hooks/useConfirmEMRPatient";
import { useAssignEMRPatient } from "src/services/emr/hooks/useAssignEMRPatient";
import { usePatientsData } from "src/services/patients/hooks/usePatientsData";
import { useAddPatient } from "src/services/patients/hooks/useAddPatient";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Types
import { UnassignedEMRType } from "src/services/emr/types";
import { AddEditPatientType } from "src/services/patients/types";

// Utils
import { getTruncatedText } from "src/utils/text/textHelper";
import { getBirthdateOnly } from "src/utils/emr/emrMatchHelper";
import { sexTypeEnums } from "src/constants/enums";

type ViewEMRModalType = {
  open: boolean;
  patientName: string;
  patientId: string;
  exam: UnassignedEMRType;
  onClose: () => void;
};

const ViewEMRModal = ({
  open,
  patientName,
  patientId,
  exam,
  onClose,
}: ViewEMRModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const borderColor = theme.palette.mode === "dark" ? "#2A2E44" : "#5B6D7A70";
  // Same treatment as PatientMatchPanel -- a distinct sunken dark surface
  // instead of inheriting the modal body's own background, with explicit
  // light text instead of relying on inherited/ambient color.
  const panelBackgroundColor =
    theme.palette.mode === "dark" ? "#202338" : undefined;
  const primaryTextColor = theme.palette.mode === "dark" ? "#FFF" : undefined;
  const { clinicInfo } = useClinicContext();

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState({
    displayName: "",
    value: "",
  });

  const {
    data: patientData,
    isLoading,
    isError,
  } = usePatientById(clinicInfo?.id, {
    patientId,
  });

  // the patient lookup resolves to an array when the id does not resolve to a
  // single patient, which the panel treats the same as a failed request
  const matchedPatient = useMemo(
    () => (Array.isArray(patientData) ? undefined : patientData),
    [patientData]
  );
  const hasMatchError = useMemo(
    () => isError || Array.isArray(patientData),
    [isError, patientData]
  );

  const {
    data: patientsOptions,
    isLoading: isLoadingPatients,
    isFetching: isFetchingPatients,
  } = usePatientsData(clinicInfo?.id);

  const {
    mutate: addMutate,
    data: addedPatient,
    isSuccess: isAddSuccess,
  } = useAddPatient(clinicInfo?.id);
  const { mutate: confirmMutate } = useConfirmEMRPatient(clinicInfo?.id);
  const { mutate: assignMutate } = useAssignEMRPatient(clinicInfo?.id);

  // After adding a new patient, select the patient
  useEffect(() => {
    if (addedPatient && isAddSuccess) {
      const patientName = getTruncatedText(
        `${addedPatient?.firstName} ${addedPatient?.lastName}`
      );
      const birthdate = addedPatient?.dateOfBirth?.slice(0, 10);
      setSelectedPatient({
        displayName: `${patientName} / ${birthdate}`,
        value: addedPatient.id,
      });
    }
  }, [addedPatient, isAddSuccess]);

  // show all patients in the dropdown except for the current patient
  const formattedPatientsOptions = useMemo(
    () =>
      (patientsOptions ?? [])
        .filter((option) => option.id !== patientId)
        .map((option) => {
          const patientName = getTruncatedText(
            `${option?.firstName} ${option?.lastName}`
          );
          const birthdate = option?.dateOfBirth?.slice(0, 10);
          return {
            displayName: `${patientName} / ${birthdate}`,
            value: option.id,
          };
        })
        .sort((a, b) => a?.displayName?.localeCompare(b?.displayName)),
    [patientsOptions, patientId]
  );

  const addPatient = useCallback(
    (patientData: AddEditPatientType) => {
      addMutate(patientData, { onSuccess: () => setOpenCreateModal(false) });
    },
    [addMutate]
  );

  const handleSetPatient = useCallback(
    (item: { displayName: string; value: string }) => {
      const value = formattedPatientsOptions?.find(
        (option) => option.value === item.value
      );

      if (value) {
        setSelectedPatient({ ...value });
      } else {
        setSelectedPatient({ displayName: "", value: "" });
      }
    },
    [formattedPatientsOptions, setSelectedPatient]
  );

  const handleClose = useCallback(() => {
    setSelectedPatient({ displayName: "", value: "" });
    onClose();
  }, [onClose]);

  const confirmMatchedPatient = useCallback(() => {
    confirmMutate({ emrJobId: exam?.jobId }, { onSuccess: handleClose });
  }, [exam?.jobId, confirmMutate, handleClose]);

  const confirmExtractedPatient = useCallback(() => {
    // no existing match, so create a patient from the extracted data
    addMutate(
      {
        firstName: exam?.patientFirstName,
        lastName: exam?.patientLastName,
        sex: exam?.patientSex ?? sexTypeEnums.UNDEFINED,
        language: clinicInfo?.settings?.clinicDefaultLanguage,
        healthCareId: exam?.patientHealthCareId ?? "",
        dateOfBirth: exam?.patientDateOfBirth ?? "",
        requiresCaptions: false,
        leftAxis: null,
        leftCylinder: null,
        leftSphere: null,
        rightAxis: null,
        rightCylinder: null,
        rightSphere: null,
      },
      {
        // wait for the new patient to exist before assigning the emr job to
        // it — addMutate is async, so assignMutate can't run until this
        // resolves and hands back the newly created patient's id
        onSuccess: (newPatient) => {
          assignMutate(
            { emrJobId: exam?.jobId, patientId: newPatient?.id },
            { onSuccess: handleClose }
          );
        },
      }
    );
  }, [
    exam?.jobId,
    exam?.patientSex,
    exam?.patientFirstName,
    exam?.patientLastName,
    exam?.patientHealthCareId,
    exam?.patientDateOfBirth,
    clinicInfo?.settings?.clinicDefaultLanguage,
    addMutate,
    assignMutate,
    handleClose,
  ]);

  const overridePatient = useCallback(() => {
    assignMutate(
      { emrJobId: exam?.jobId, patientId: selectedPatient?.value },
      {
        onSuccess: handleClose,
      }
    );
  }, [exam?.jobId, selectedPatient?.value, assignMutate, handleClose]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography fontWeight={600} fontSize="16px">
            {patientName}
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            {t("Birthdate")}:{" "}
            {getBirthdateOnly(exam?.patientDateOfBirth) || "N/A"}
          </Typography>
        </Box>
      }
      maxWidth="md"
    >
      <Grid container item xs={12} display="flex">
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            order: { xs: 2, md: 1 },
          }}
        >
          <Box sx={{ width: "100%", maxHeight: "70vh", overflowY: "scroll" }}>
            {exam?.fileUrl ? (
              <ExternalExamIframe examUrl={exam?.fileUrl} />
            ) : (
              <Box sx={{ p: "12px", width: "100%" }}>
                <Alert
                  variant="warning"
                  alertContent={t("No Exam File Available.")}
                />
              </Box>
            )}
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{
            p: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            order: { xs: 1, md: 2 },
          }}
        >
          <Box sx={{ display: "flex", gap: "12px" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                border: `1px solid ${borderColor}`,
                borderRadius: "4px",
                backgroundColor: panelBackgroundColor,
                p: "12px",
                flex: 1,
              }}
            >
              <Typography sx={{ fontSize: "14px", color: primaryTextColor }}>
                {t("Source")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: primaryTextColor,
                }}
              >
                {exam.platformType}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                border: `1px solid ${borderColor}`,
                borderRadius: "4px",
                backgroundColor: panelBackgroundColor,
                p: "12px",
                flex: 1,
              }}
            >
              <Typography sx={{ fontSize: "14px", color: primaryTextColor }}>
                {t("Exam Type")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: primaryTextColor,
                }}
              >
                {t(exam.examType)}
              </Typography>
            </Box>
          </Box>

          <PatientMatchPanel
            exam={exam}
            matchedPatient={matchedPatient}
            isLoading={isLoading}
            isError={hasMatchError}
            onKeepMatched={confirmMatchedPatient}
            onKeepExtracted={confirmExtractedPatient}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${borderColor}`,
              borderRadius: "4px",
              backgroundColor: panelBackgroundColor,
              p: "12px",
              gap: "8px",
            }}
          >
            <Typography sx={{ fontSize: "14px", color: primaryTextColor }}>
              {t("Choose a Different Patient")}
            </Typography>
            <CustomAutocomplete
              id="view-emr-patient-autocomplete"
              data={formattedPatientsOptions}
              value={selectedPatient}
              onChange={handleSetPatient}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <Button
                startIcon={<PlusIcon />}
                sx={ModalCustomConfirmLink}
                onClick={() => setOpenCreateModal(true)}
              >
                {t("Add Patient")}
              </Button>
              <Button
                sx={[
                  ModalCustomConfirmDarkCta,
                  // The disabled state needs its own override too, since
                  // ModalCustomConfirmDark's own .Mui-disabled rule (a dim
                  // translucent navy) would otherwise still win there.
                  (sxTheme) => ({
                    ...(sxTheme.palette.mode === "dark" && {
                      "&.Mui-disabled": {
                        color: "#FFF",
                        backgroundColor: "#E2772E60",
                      },
                    }),
                  }),
                ]}
                disabled={
                  !selectedPatient.value ||
                  isLoadingPatients ||
                  isFetchingPatients
                }
                onClick={overridePatient}
              >
                {t("Confirm")}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <CreateEMRPatientModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onConfirm={addPatient}
        allPatients={patientsOptions}
        exam={exam}
      />
    </DefaultModal>
  );
};

export default ViewEMRModal;
